import React from 'react';
import { MessageCircle, Loader } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import PostCard from './PostCard';
import PostedCard from './PostedCard';

/**
 * POSTComponent — Container that renders a create-post form followed by a list of posts.
 *
 * Props:
 * - posts               — array of post objects (required)
 * - loading             — boolean (required)
 * - canPost             — show the create-post form (optional, default true)
 * - onCreatePost(text, imageUrls, privacy) → Promise<{success, error}> (required when canPost)
 * - onReactToPost(postId, type) — reaction handler (required)
 * - onDeletePost(postId) — delete handler (optional)
 * - currentUserId       — string (optional)
 * - placeholder         — textarea placeholder for PostCard (optional)
 * - maxLength           — character limit for PostCard (optional)
 * - defaultPrivacy      — 'public' | 'private' for PostCard (optional)
 * - emptyMessage        — message shown when posts list is empty (optional)
 */
const POSTComponent = ({
  posts = [],
  loading = false,
  canPost = true,
  onCreatePost,
  onReactToPost,
  onDeletePost,
  onPostUpdated,
  currentUserId,
  isAdmin = false,
  placeholder,
  maxLength,
  defaultPrivacy,
  emptyMessage = 'No posts yet',
  allowVideoUpload = false,
}) => {
  const { theme } = useTheme();

  return (
    <div className="space-y-6">
      {/* Create Post */}
      {canPost && onCreatePost && (
        <PostCard
          onSubmit={onCreatePost}
          onPostUpdated={onPostUpdated}
          placeholder={placeholder}
          maxLength={maxLength}
          defaultPrivacy={defaultPrivacy}
          allowVideoUpload={allowVideoUpload}
        />
      )}

      {/* Loading State */}
      {loading && posts.length === 0 && (
        <div className={`${theme.card} rounded-lg p-8 text-center`}>
          <div className="flex justify-center py-4">
            <Loader className="w-8 h-8 animate-spin text-blue-500" />
          </div>
        </div>
      )}

      {/* Empty State */}
      {!loading && posts.length === 0 && (
        <div className={`${theme.card} rounded-lg p-12 text-center`}>
          <MessageCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className={`text-xl font-semibold ${theme.text} mb-2`}>No posts yet</h2>
          <p className={`${theme.textMuted}`}>{emptyMessage}</p>
        </div>
      )}

      {/* Posts Feed */}
      <div className="space-y-6">
        {posts.map(post => (
          <PostedCard
            key={post.id}
            post={post}
            onReact={onReactToPost}
            onDelete={onDeletePost}
            currentUserId={currentUserId}
            isAdmin={isAdmin}
          />
        ))}
      </div>
    </div>
  );
};

export default POSTComponent;
